import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { SignaturePad } from "./SignaturePad";
import { Eraser, PenLine, Type } from "lucide-react";

/**
 * SignatureField — type it or draw it.
 *
 * Typing is the default because almost nobody can draw their own signature with a mouse: the
 * name is set in a handwriting face, shown at the size it will appear, and rasterised to the
 * same PNG the drawn pad produces. So an executed PDF carries a signature that looks written
 * rather than a line of typed Helvetica.
 *
 * ref API: isEmpty(), toDataURL(), name
 */
export const SignatureField = forwardRef(({ name, onNameChange, testId = "signature" }, ref) => {
  const [mode, setMode] = useState("type");
  const [padEmpty, setPadEmpty] = useState(true);
  const padRef = useRef(null);
  const typed = (name || "").trim();

  const renderTyped = () => {
    const scale = 3;
    const w = 460; const h = 130;
    const c = document.createElement("canvas");
    c.width = w * scale; c.height = h * scale;
    const ctx = c.getContext("2d");
    ctx.scale(scale, scale);
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "#0B1220";
    ctx.textBaseline = "alphabetic";
    let size = 62;
    ctx.font = `600 ${size}px Caveat, cursive`;
    while (ctx.measureText(typed).width > w - 40 && size > 24) {
      size -= 2;
      ctx.font = `600 ${size}px Caveat, cursive`;
    }
    ctx.translate(20, h - 38);
    ctx.rotate(-0.025);
    ctx.fillText(typed, 0, 0);
    // The underline a pen leaves — drawn, not a border, so it travels into the PDF.
    ctx.strokeStyle = "rgba(11,18,32,0.55)";
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(2, 10);
    ctx.lineTo(Math.min(w - 40, ctx.measureText(typed).width + 10), 10);
    ctx.stroke();
    return c.toDataURL("image/png");
  };

  useImperativeHandle(ref, () => ({
    isEmpty: () => (mode === "type" ? typed.length < 2 : padRef.current?.isEmpty() !== false),
    toDataURL: () => (mode === "type" ? renderTyped() : padRef.current?.toDataURL()),
  }));

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="inline-flex rounded-[11px] border border-[#CBD5E1] bg-white p-0.5">
        {[["type", "Type it", Type], ["draw", "Draw it", PenLine]].map(([k, label, Icon]) => (
          <button key={k} type="button" onClick={() => setMode(k)}
                  className={`inline-flex h-9 items-center gap-2 rounded-[9px] px-3.5 text-[12.5px] font-bold transition-colors ${
                    mode === k ? "bg-[#0B1220] text-white" : "text-slate-500 hover:text-[#0B1220]"}`}
                  data-testid={`${testId}-mode-${k}`}>
            <Icon className="size-3.5" /> {label}
          </button>
        ))}
      </div>

      {mode === "type" ? (
        <div>
          <Input value={name} onChange={(e) => onNameChange(e.target.value)} placeholder="e.g. Kieran Flannery"
                 className="h-11" data-testid={`${testId}-name`} />
          <div className="relative mt-3 grid h-36 place-items-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-6">
            {typed.length > 1 ? (
              <div className="w-full text-center">
                <div className="font-sig text-[46px] leading-none text-[#0B1220] [transform:rotate(-1.4deg)]" data-testid={`${testId}-preview`}>
                  {typed}
                </div>
                <div className="mx-auto mt-2 h-px w-2/3 bg-[#0B1220]/40" />
              </div>
            ) : (
              <span className="text-[12.5px] italic text-slate-400">Type your full name and it appears here as your signature</span>
            )}
          </div>
          <div className="mt-2 text-[11px] text-slate-400">
            This exact mark is embedded in the executed PDF, with your name, the date, your IP address and the document version.
          </div>
        </div>
      ) : (
        <div>
          <SignaturePad ref={padRef} onChange={setPadEmpty} testId={`${testId}-pad`} />
          <Input value={name} onChange={(e) => onNameChange(e.target.value)} placeholder="Full legal name"
                 className="mt-3 h-11" data-testid={`${testId}-name-draw`} />
          {!padEmpty && (
            <button type="button" onClick={() => padRef.current?.clear()}
                    className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-500 hover:text-[#0B1220]"
                    data-testid={`${testId}-clear`}>
              <Eraser className="size-3" /> Clear and start again
            </button>
          )}
        </div>
      )}
    </div>
  );
});
SignatureField.displayName = "SignatureField";
